// src/components/work_orders/AddInventoryToWorkOrderModal.js
import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Select, VStack, HStack, IconButton,
  useToast, useColorModeValue, Text, NumberInput, NumberInputField,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import inventoryService from '../../services/inventoryService';
import workOrderInventoryService from '../../services/workOrderInventoryService';

// --- UPDATE: onSaved(items) lets the parent refresh; quantities sent as numbers; stock + duplicate checks
const AddInventoryToWorkOrderModal = ({ isOpen, onClose, workOrder, onSaved }) => {
  const [items, setItems] = useState([{ item_id: '', quantity_used: 1 }]);
  const [availableInventory, setAvailableInventory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const modalBg = useColorModeValue("white", "navy.800");

  useEffect(() => {
    if (isOpen) {
      setItems([{ item_id: '', quantity_used: 1 }]); // --- UPDATE: fresh rows each time
      inventoryService.getAllItems()
        .then(data => setAvailableInventory(data || []))
        .catch(() => toast({ title: 'Error fetching inventory.', status: 'error' }));
    }
  }, [isOpen, toast]);

  const handleItemChange = (index, e) => {
    const newItems = [...items];
    newItems[index][e.target.name] = e.target.value;
    setItems(newItems);
  };

  const handleItemNumberChange = (index, value) => {
    const newItems = [...items];
    newItems[index].quantity_used = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { item_id: '', quantity_used: 1 }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // --- UPDATE: numbers, merged duplicates, stock check
    const merged = new Map();
    for (const it of items) {
      const qty = Number(it.quantity_used);
      if (!it.item_id || !Number.isFinite(qty) || qty <= 0) {
        toast({ title: 'Please fill all item fields correctly.', status: 'warning' });
        return;
      }
      merged.set(String(it.item_id), (merged.get(String(it.item_id)) || 0) + qty);
    }
    const payload = [...merged.entries()].map(([item_id, quantity_used]) => {
      const inv = availableInventory.find(i => String(i.id) === item_id);
      return { item_id: inv ? inv.id : item_id, quantity_used };
    });
    const short = payload.filter(p => {
      const inv = availableInventory.find(i => String(i.id) === String(p.item_id));
      return inv && Number.isFinite(Number(inv.quantity)) && p.quantity_used > Number(inv.quantity);
    });
    if (short.length) {
      const names = short.map(p => availableInventory.find(i => String(i.id) === String(p.item_id))?.name).join(', ');
      toast({ title: 'Not enough stock', description: `${names}: quantity used is more than is in stock.`, status: 'warning', duration: 5000 });
      return;
    }
    // --- END UPDATE
    setIsSubmitting(true);
    try {
      await workOrderInventoryService.addItemsToWorkOrder(workOrder.id, payload); // --- UPDATE
      toast({ title: 'Inventory usage recorded.', status: 'success' });
      onSaved?.(payload); // --- UPDATE
      onClose();
    } catch (error) {
      toast({ title: 'Error saving inventory usage.', description: error.message, status: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent bg={modalBg}>
        <ModalHeader>Add Inventory Used for WO #{String(workOrder?.id ?? '').slice(0, 12)}</ModalHeader>{/* --- UPDATE: null-safe */}
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Text>Select the items and quantities used to complete this work order. This will automatically update stock levels and the audit history.</Text>
            {items.map((item, index) => (
              <HStack key={index} w="100%" spacing={2}>
                <FormControl isRequired flex={3}>
                  <Select name="item_id" placeholder="Select Item" value={item.item_id} onChange={(e) => handleItemChange(index, e)}>
                    {availableInventory.map(inv => (
                      <option key={inv.id} value={inv.id}>{inv.name} (In Stock: {inv.quantity})</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" m={0} px={2}>Qty Used</FormLabel>
                  <NumberInput min={1} value={item.quantity_used} onChange={(val) => handleItemNumberChange(index, val)}>
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <IconButton icon={<DeleteIcon />} colorScheme="red" variant="ghost" onClick={() => removeItem(index)} isDisabled={items.length === 1} aria-label="Remove item" />{/* --- UPDATE: a11y */}
              </HStack>
            ))}
            <Button size="sm" leftIcon={<AddIcon />} onClick={addItem} alignSelf="flex-start">Add Another Item</Button>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>Save Usage</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddInventoryToWorkOrderModal;
